// import React from "react";
// import { useArchives } from "../../../lib/firebase/archives/read";
// import { Link } from "react-router-dom";
// import { Alert, Spinner, Table } from "react-bootstrap";
// import { Edit, Trash } from "lucide-react";

// const ArchiveListView = () => {
//   const { data, isLoading, error } = useArchives();

//   return (
//     <main className="container py-4">
//       <div className="mb-3 d-flex justify-content-between align-items-center">
//         <h1 className="h4">Archives</h1>
//         <Link to="/admin/archives/form" className="btn btn-primary">
//           Add Archive
//         </Link>
//       </div>

//       {isLoading && (
//         <div className="text-center">
//           <Spinner animation="border" />
//         </div>
//       )}

//       {error && (
//         <Alert variant="danger" className="text-center">
//           {error}
//         </Alert>
//       )}

//       {!isLoading && !error && (!data || data.length === 0) && (
//         <Alert variant="info" className="text-center">
//           No Archives Found
//         </Alert>
//       )}

//       {!isLoading && !error && data && data.length > 0 && (
//         <Table responsive striped bordered hover>
//           <thead>
//             <tr>
//               <th>Sr. No.</th>
//               <th>Published By</th>
//               <th>Category</th>
//               <th>Meta Title</th>
//               <th>Meta Description</th>
//               <th>Month-Year</th>
//               <th>Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {data.map((item, index) => (
//               <tr key={item?.id}>
//                 <td>{index + 1}</td>
//                 <td>{item?.publishedBy}</td>
//                 <td>{item?.categoryId}</td>
//                 <td>{item?.metaTitle}</td>
//                 <td>{item?.metaDescription}</td>
//                 <td>{item?.monthYear}</td>
//                 <td>
//                   <div className="d-flex gap-2">
//                     <Link
//                       to={`/admin/archives/form?id=${item?.id}&monthYear=${item?.monthYear}`}
//                       className="btn btn-warning btn-sm"
//                     >
//                       <Edit size={16} />
//                     </Link>
//                     <button
//                       className="btn btn-danger btn-sm"
//                       onClick={() => {
//                         if (
//                           window.confirm(
//                             "Are you sure you want to delete this archive?"
//                           )
//                         ) {
//                           // Implement delete logic if needed
//                         }
//                       }}
//                     >
//                       <Trash size={16} />
//                     </button>
//                   </div>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </Table>
//       )}
//     </main>
//   );
// };

// export default ArchiveListView;

import React, { useState, useMemo } from "react";
import { useArchives } from "../../../lib/firebase/archives/read";
import { Link } from "react-router-dom";
import { Alert, Spinner, Table, Pagination, Form } from "react-bootstrap";
import { Edit, CirclePlus, ArrowUp, ArrowDown } from "lucide-react";

const ArchiveListView = () => {
  const { data, isLoading, error } = useArchives();
  const [sortDirection, setSortDirection] = useState("desc"); // Default: newest to oldest
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5); // Default: 5 rows per page

  // Format month-year to lowercase (e.g., may-2025)
  const formatMonthYear = (monthYear) => {
    if (!monthYear) return "";
    return monthYear.toLowerCase();
  };

  // Sort archives by month-year
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const monthMap = {
      january: 1,
      february: 2,
      march: 3,
      april: 4,
      may: 5,
      june: 6,
      july: 7,
      august: 8,
      september: 9,
      october: 10,
      november: 11,
      december: 12,
    };

    return [...data].sort((a, b) => {
      const [aMonth, aYear] = a.monthYear.toLowerCase().split("-");
      const [bMonth, bYear] = b.monthYear.toLowerCase().split("-");

      const aDate = new Date(
        parseInt(aYear),
        monthMap[aMonth.toLowerCase()] - 1
      );
      const bDate = new Date(
        parseInt(bYear),
        monthMap[bMonth.toLowerCase()] - 1
      );

      return sortDirection === "desc"
        ? bDate - aDate // Newest to oldest
        : aDate - bDate; // Oldest to newest
    });
  }, [data, sortDirection]);

  // Pagination logic
  const totalRows = sortedData.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
  const currentData = sortedData.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    console.log("Page changed to:", page);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (e) => {
    const newRowsPerPage = parseInt(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page
    console.log("Rows per page changed to:", newRowsPerPage);
  };

  // Toggle sort direction
  const handleSortToggle = () => {
    setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
    setCurrentPage(1); // Reset to first page on sort
    console.log("Sort toggled to:", sortDirection === "desc" ? "asc" : "desc");
  };

  // Generate pagination items
  const getPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5; // Show up to 5 page numbers

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page
    items.push(
      <Pagination.First
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
        key="first"
      />
    );

    // Previous page
    items.push(
      <Pagination.Prev
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        key="prev"
      />
    );

    // Page numbers
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Next page
    items.push(
      <Pagination.Next
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        key="next"
      />
    );

    // Last page
    items.push(
      <Pagination.Last
        onClick={() => handlePageChange(totalPages)}
        disabled={currentPage === totalPages}
        key="last"
      />
    );

    return items;
  };

  return (
    <main className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h4">Archives</h1>
        <Link to="/admin/archives/form" className="btn btn-primary">
          <CirclePlus className="me-1" size={16} />
          Add Archive
        </Link>
      </div>

      {isLoading && (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      )}

      {error && (
        <Alert variant="danger" className="text-center">
          {error}
        </Alert>
      )}

      {!isLoading && !error && (!data || data.length === 0) && (
        <Alert variant="info" className="text-center">
          No Archives Found
        </Alert>
      )}

      {!isLoading && !error && currentData && currentData.length > 0 && (
        <>
          {/* Table */}
          <div className="table-responsive">
            <Table
              striped
              bordered
              hover
              className="bg-white rounded shadow-sm"
            >
              <thead>
                <tr>
                  <th className="py-2 px-3">Sr. No.</th>
                  <th className="py-2 px-3">Published By</th>
                  <th className="py-2 px-3">Category</th>
                  <th
                    onClick={handleSortToggle}
                    style={{ cursor: "pointer" }}
                    className="py-2 px-3 d-flex align-items-center"
                  >
                    Month-Year
                    {sortDirection === "desc" ? (
                      <ArrowDown size={16} className="ms-1" />
                    ) : (
                      <ArrowUp size={16} className="ms-1" />
                    )}
                  </th>
                  <th className="py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, index) => (
                  <tr key={item?.id}>
                    <td className="py-2 px-3">{startIndex + index + 1}</td>
                    <td className="py-2 px-3">{item?.publishedBy}</td>
                    <td className="py-2 px-3">{item?.categoryId}</td>
                    <td className="py-2 px-3">
                      {formatMonthYear(item?.monthYear)}
                    </td>
                    <td className="py-2 px-3">
                      <div className="d-flex gap-2">
                        <Link
                          to={`/admin/archives/form?id=${item?.id}&monthYear=${item?.monthYear}`}
                          className="btn btn-warning btn-sm"
                        >
                          <Edit size={16} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Pagination Controls */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
            {/* Rows per page selector */}
            <div className="mb-3 mb-md-0">
              <Form.Group className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0">Rows per page:</Form.Label>
                <Form.Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  style={{ width: "auto" }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </Form.Select>
              </Form.Group>
            </div>

            {/* Pagination info and controls */}
            <div className="d-flex flex-column flex-sm-row align-items-center gap-3">
              <span>
                Showing {startIndex + 1} to {endIndex} of {totalRows} entries
              </span>
              <Pagination className="mb-0">{getPaginationItems()}</Pagination>
            </div>
          </div>
        </>
      )}
    </main>
  );
};

export default ArchiveListView;
